'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { Button, IconButton } from '../../../components/actions/button/index.js';
import { Spinner } from '../../../components/feedback/spinner/index.js';
import { cx } from '../../../internal/cx.js';
import { allowValue, documentBase, exactOrigins, originOf, sandboxValue, } from '../../../internal/shell/embed.js';
import { ChatGlyph, CloseGlyph } from '../../../internal/shell/panel-icons.js';
import { chatPageContext, cleanOpenDetail, createChatDockBridge, } from './chat-dock.protocol.js';
const DEFAULT_MESSAGES = {
    label: 'Rook Chat',
    unread: (n) => `${n} unread`,
    loading: 'Loading chat…',
    incomingCall: 'Incoming call',
    answer: 'Open',
    dismiss: 'Dismiss',
};
/**
 * Sandbox for the Chat frame: scripts and its own session, forms, links that
 * open in a new tab and file downloads. No top navigation, no modals.
 */
export const DEFAULT_CHAT_SANDBOX = [
    'allow-scripts',
    'allow-same-origin',
    'allow-forms',
    'allow-popups',
    'allow-popups-to-escape-sandbox',
    'allow-downloads',
];
/** Calls, screen sharing, fullscreen video, picture-in-picture and copy/paste. */
export const DEFAULT_CHAT_ALLOW = [
    'camera',
    'microphone',
    'display-capture',
    'fullscreen',
    'picture-in-picture',
    'autoplay',
    'clipboard-read',
    'clipboard-write',
];
/**
 * The Rook Chat dock: a launcher with an unread badge and the panel that holds
 * the real Rook Chat app in an iframe (`rook-chat-embed` v1). The frame is
 * created on first open (or on hover) and then kept alive, so calls and
 * drafts survive closing the panel. The dock never fetches: unread counts and
 * session refreshes come from the product and the frame.
 */
export const ChatDock = forwardRef(function ChatDock({ src, allowedOrigins, appName, workspaceId, refreshSession, hidden = false, unread: unreadProp, onUnreadChange, open: openProp, defaultOpen = false, onOpenChange, context, mountOnIntent = true, icon, sandbox = DEFAULT_CHAT_SANDBOX, allow = DEFAULT_CHAT_ALLOW, ringTimeout = 30_000, onStatusChange, onLayoutChange, onLocationChange, onEmbedMessage, controllerRef, messages: messagesProp, className, ...rest }, ref) {
    const messages = useMemo(() => ({ ...DEFAULT_MESSAGES, ...messagesProp }), [messagesProp]);
    const panelId = `${useId()}-chat-panel`;
    const frameRef = useRef(null);
    const launcherRef = useRef(null);
    const [innerOpen, setInnerOpen] = useState(defaultOpen);
    const open = openProp ?? innerOpen;
    const [mounted, setMounted] = useState(open);
    const [ready, setReady] = useState(false);
    const [status, setStatus] = useState('ok');
    const [layout, setLayout] = useState('panel');
    const [frameUnread, setFrameUnread] = useState(null);
    const [ring, setRing] = useState(null);
    const [base, setBase] = useState(undefined);
    useEffect(() => setBase(documentBase()), []);
    const target = originOf(src, base);
    const allowedKey = allowedOrigins.join('\n');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const allowed = useMemo(() => exactOrigins(allowedOrigins), [allowedKey]);
    const latest = useRef({
        appName,
        workspaceId,
        refreshSession,
        context,
        onOpenChange,
        onUnreadChange,
        onStatusChange,
        onLayoutChange,
        onLocationChange,
        onEmbedMessage,
    });
    useEffect(() => {
        latest.current = {
            appName,
            workspaceId,
            refreshSession,
            context,
            onOpenChange,
            onUnreadChange,
            onStatusChange,
            onLayoutChange,
            onLocationChange,
            onEmbedMessage,
        };
    });
    const setOpen = useCallback((next) => {
        if (openProp === undefined)
            setInnerOpen(next);
        latest.current.onOpenChange?.(next);
    }, [openProp]);
    const focusLauncher = () => requestAnimationFrame(() => launcherRef.current?.focus());
    // One bridge per frame origin; created lazily, never while rendering.
    const bridgeRef = useRef(null);
    const getBridge = useCallback(() => {
        const key = `${target ?? ''}\u0000${allowedKey}`;
        if (!bridgeRef.current || bridgeRef.current.key !== key) {
            bridgeRef.current = {
                key,
                bridge: createChatDockBridge({
                    frameWindow: () => frameRef.current?.contentWindow ?? null,
                    allowedOrigins: allowed,
                    targetOrigin: target,
                    appName: () => latest.current.appName,
                    workspaceId: () => latest.current.workspaceId,
                    refresh: () => latest.current.refreshSession(),
                    context: () => (latest.current.context ?? (() => chatPageContext(latest.current.appName)))(),
                    on: (m) => {
                        switch (m.type) {
                            case 'rook-chat:ready':
                                setReady(true);
                                break;
                            case 'rook-chat:unread':
                                setFrameUnread(m.count);
                                latest.current.onUnreadChange?.(m.count);
                                break;
                            case 'rook-chat:layout':
                                setLayout(m.layout);
                                latest.current.onLayoutChange?.(m.layout);
                                break;
                            case 'rook-chat:ring':
                                setRing({ callId: m.callId, conversationId: m.conversationId });
                                break;
                            case 'rook-chat:close':
                                closeRef.current();
                                break;
                            case 'rook-chat:status':
                                setStatus(m.status);
                                latest.current.onStatusChange?.(m.status);
                                break;
                            case 'rook-chat:location':
                                latest.current.onLocationChange?.(m.conversationId);
                                break;
                            default:
                                break;
                        }
                        latest.current.onEmbedMessage?.(m);
                    },
                }),
            };
        }
        return bridgeRef.current.bridge;
    }, [target, allowedKey, allowed]);
    useEffect(() => {
        const onMessage = (e) => void getBridge().handle(e);
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [getBridge]);
    // A new frame (other workspace or embed URL) must say "ready" again.
    const frameKey = `${src}\u0000${workspaceId}`;
    useEffect(() => {
        getBridge().reset();
        setReady(false);
        setFrameUnread(null);
        setLayout('panel');
    }, [frameKey, getBridge]);
    const inCall = layout === 'call' || layout === 'fullscreen';
    const shown = status !== 'unavailable' && (!hidden || inCall);
    const panelVisible = shown && open && mounted;
    const close = useCallback(() => {
        setOpen(false);
        focusLauncher();
    }, [setOpen]);
    const closeRef = useRef(close);
    closeRef.current = close;
    const openDock = useCallback((detail) => {
        setMounted(true);
        setOpen(true);
        setRing(null);
        const clean = cleanOpenDetail(detail);
        if (clean.conversationId || clean.share || clean.create)
            getBridge().post({ type: 'rook-chat:open', ...clean });
        requestAnimationFrame(() => frameRef.current?.focus());
    }, [getBridge, setOpen]);
    useImperativeHandle(controllerRef, () => ({ open: openDock, close, post: (m) => getBridge().post(m) }), [openDock, close, getBridge]);
    // Open from outside (controlled `open`) mounts the frame too.
    useEffect(() => {
        if (open)
            setMounted(true);
    }, [open]);
    // Keep the frame told whether it is on screen.
    useEffect(() => {
        if (ready)
            getBridge().post({ type: 'rook-chat:visibility', open: panelVisible });
    }, [ready, panelVisible, getBridge]);
    // A ring nobody answers goes away on its own.
    useEffect(() => {
        if (!ring || !(ringTimeout > 0))
            return;
        const t = setTimeout(() => setRing(null), ringTimeout);
        return () => clearTimeout(t);
    }, [ring, ringTimeout]);
    const unread = frameUnread ?? Math.max(0, Math.floor(unreadProp ?? 0));
    const launcherName = unread > 0 ? `${messages.label}, ${messages.unread(unread)}` : messages.label;
    return (_jsxs("div", { ref: ref, ...rest, className: cx('mn-chat-dock', className), "data-layout": layout, "data-open": panelVisible || undefined, "data-hidden": !shown || undefined, children: [mounted ? (
            // The panel is a (non-modal) dialog: Escape on it closes the dock.
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            _jsxs("section", { id: panelId, className: "mn-chat-dock__panel", role: "dialog", "aria-label": messages.label, "aria-hidden": !panelVisible, "data-visible": panelVisible || undefined, onKeyDown: (e) => {
                    if (e.key === 'Escape' && layout !== 'fullscreen') {
                        e.stopPropagation();
                        close();
                    }
                }, children: [_jsx("iframe", { ref: frameRef, className: "mn-chat-dock__frame", src: src, title: messages.label, sandbox: sandboxValue(sandbox), allow: allowValue(allow), allowFullScreen: true, referrerPolicy: "strict-origin-when-cross-origin", tabIndex: panelVisible ? undefined : -1 }, frameKey), !ready ? (_jsx("div", { className: "mn-chat-dock__loading", children: _jsx(Spinner, { size: "md", ...(panelVisible ? { label: messages.loading } : {}) }) })) : null] })) : null, shown && ring && !panelVisible ? (_jsxs("div", { className: "mn-chat-dock__ring", role: "alert", children: [_jsx("span", { className: "mn-chat-dock__ring-dot", "aria-hidden": true }), _jsx("span", { className: "mn-chat-dock__ring-text", children: messages.incomingCall }), _jsx(Button, { size: "sm", variant: "primary", onClick: () => openDock({ conversationId: ring.conversationId }), children: messages.answer }), _jsx(IconButton, { size: "sm", label: messages.dismiss, icon: _jsx(CloseGlyph, {}), onClick: () => setRing(null) })] })) : null, shown ? (_jsxs("button", { ref: launcherRef, type: "button", className: "mn-chat-dock__launcher", "aria-label": launcherName, title: launcherName, "aria-expanded": panelVisible, "aria-controls": mounted ? panelId : undefined, onPointerEnter: mountOnIntent ? () => setMounted(true) : undefined, onFocus: mountOnIntent ? () => setMounted(true) : undefined, onClick: () => (panelVisible ? close() : openDock()), children: [_jsx("span", { className: "mn-chat-dock__icon", "aria-hidden": true, children: icon ?? _jsx(ChatGlyph, {}) }), unread > 0 ? (_jsx("span", { className: "mn-chat-dock__badge", "aria-hidden": true, children: unread > 99 ? '99+' : unread })) : null] })) : null] }));
});
//# sourceMappingURL=chat-dock.js.map